/*
 * captureMapping.js — the SINGLE place that defines what a check-in capture
 * writes, shared by both the browser client and the Notion proxy server so the
 * two can never drift.
 *
 * Refinement #2 (approved): Evidence.Source is NOT hardcoded as "Text"
 * throughout the codebase. It lives here, once. The live Notion "Evidence
 * Record" select currently offers [Voice, Text, Photo, Email, Call, Document]
 * with no "Check-In" option, so an app-tap check-in maps most honestly to
 * "Text". When the Founder adds a "Check-In" option to that select, change the
 * ONE line below and nothing else.
 */
export const CHECK_IN_EVIDENCE_SOURCE = 'Text' // ← change to 'Check-In' once the Notion select has it

// Enum values that must match the live Notion schema exactly.
export const CONFIDENCE = { HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' }
export const AUDIT_ACTION = { CREATE: 'Create', UPDATE: 'Update' }
export const AUDIT_OUTCOME = { SUCCESS: 'Success' }
// Working / Checked Out / Day Closed per FD-001 (CP-001 → Field Discovery
// Queue) — value extension of the existing select, no new entity.
export const LABOR_PROGRESS = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress', // legacy pre-FD-001 value; kept for old rows
  COMPLETE: 'Complete',
  WORKING: 'Working',
  CHECKED_OUT: 'Checked Out',
  DAY_CLOSED: 'Day Closed',
  // Canonical Workday Void: record preserved, invalid for active/payable use.
  VOIDED: 'Voided',
}

// FD-001 reporting window. Configurable constant for now; office-level
// configuration arrives with the office surfaces.
export const REPORTING_WINDOW_MINUTES = 30
export const PENDING_STATUS = { PENDING: 'Pending' }
export const PENDING_REASON = {
  UNCLEAR_PROJECT: 'Unclear Project',
  UNCLASSIFIED: 'Unclassified',
}
export const RESOURCE_TYPE_LABOR = 'Daily Labor Entry'

/**
 * Decide whether a check-in can be captured cleanly or must be held on the
 * Pending Desk. Per CP-001: low identity confidence OR an unmatched project is
 * never force-classified and never dropped — it is held, with a reason.
 */
export function pendingReason(session) {
  if (!session.employee?.id) return PENDING_REASON.UNCLASSIFIED
  if (session.confidence === CONFIDENCE.LOW) return PENDING_REASON.UNCLASSIFIED
  if (!session.project?.id) return PENDING_REASON.UNCLEAR_PROJECT
  return null // clean happy path
}

/**
 * Build the neutral intermediate representation (IR) of everything a check-in
 * writes. The server translates this IR into Notion properties; the fixture
 * adapter stores it as-is. Keeping the IR here means "what a check-in records"
 * is defined in exactly one place.
 *
 * @param {object} session  { employee:{id,name}, project:{id,name}|null, confidence, tenantId }
 * @param {string} capturedAtISO  client capture time (authoritative for offline)
 */
export function buildCheckInRecords(session, capturedAtISO) {
  const held = pendingReason(session)
  const projectName = session.project?.name ?? 'unassigned project'
  const timeLabel = formatTimeLabel(capturedAtISO)

  if (held) {
    // Held capture — nothing is lost, but it is not finalized as a labor entry.
    // A self-reported name (entered by hand, no Employee record matched) is
    // carried in the raw capture so the office can resolve the identity.
    const selfReported = session.employee?.name && !session.employee?.id
      ? ` Self-reported name: "${session.employee.name}".`
      : ''
    // Same treatment for a project typed by hand while offline (no Project
    // record could be created yet) — the office resolves it from the name.
    const selfProject = session.project?.name && !session.project?.id
      ? ` Self-reported project: "${session.project.name}".`
      : ''
    return {
      held: true,
      reasonHeld: held,
      pendingDeskItem: {
        name: `Check-in held — ${session.employee?.name ?? 'unknown member'}`,
        rawCapture: `Check-in at ${capturedAtISO} for ${projectName}. Held: ${held}.${selfReported}${selfProject}`,
        capturedById: session.employee?.id ?? null,
        relatedProjectId: session.project?.id ?? null,
        reasonHeld: held,
        status: PENDING_STATUS.PENDING,
      },
      capturedAt: capturedAtISO,
    }
  }

  return {
    held: false,
    // Same entry End-of-Day later completes — created here "In Progress".
    dailyLaborEntry: {
      name: `${session.employee.name} — ${projectName} — ${dateLabel(capturedAtISO)}`,
      employeeId: session.employee.id,
      projectId: session.project.id,
      dateISO: capturedAtISO,
      // ET-3: authoritative Event Time, full captured precision, write-once.
      checkedInAtEvent: capturedAtISO,
      operationalProgress: LABOR_PROGRESS.WORKING, // FD-001
    },
    // Every capture creates an Evidence Record (source, confidence, timestamp).
    evidence: {
      name: `Check-in — ${session.employee.name}`,
      capturedById: session.employee.id,
      relatedProjectId: session.project.id,
      confidence: session.confidence ?? CONFIDENCE.HIGH,
      source: CHECK_IN_EVIDENCE_SOURCE,
      // GPS is informational metadata only — recorded silently when available,
      // never required, never blocking (not Attendance Integrity).
      rawContent: `Checked in — ${projectName}, ${timeLabel}${gpsSuffix(session.gps)}`,
    },
    // Every material capture auto-generates an Audit Record (who, when, what).
    audit: {
      name: `Check-in — ${session.employee.name}`,
      actorId: session.employee.id,
      action: AUDIT_ACTION.CREATE,
      outcome: AUDIT_OUTCOME.SUCCESS,
      resourceType: RESOURCE_TYPE_LABOR,
      // resourceReference is filled in with the created labor-entry id by the writer.
    },
    capturedAt: capturedAtISO,
  }
}

/*
 * Optional post-check-in captures (both Capture-class, both optional — closing
 * the app without them is a complete path).
 */

// Photo evidence note. The photo binary stays on the device in this pass; the
// Evidence Record states that honestly rather than pretending an upload.
export const PHOTO_EVIDENCE_SOURCE = 'Photo'

export function buildPhotoEvidence(session, capturedAtISO, fileName) {
  return {
    name: `Photo — ${session.employee?.name ?? 'unknown member'}`,
    capturedById: session.employee?.id ?? null,
    relatedProjectId: session.project?.id ?? null,
    confidence: session.confidence ?? CONFIDENCE.HIGH,
    source: PHOTO_EVIDENCE_SOURCE,
    rawContent: `Photo captured ${formatTimeLabel(capturedAtISO)}${fileName ? ` (${fileName})` : ''} — stored on device; file sync arrives with End-of-Day capture.`,
  }
}

// Issue report → Pending Desk. The reporter never classifies it (CP-001:
// resolved into a formal Risk later by review, not at capture time).
export function buildIssuePending(session, capturedAtISO, text) {
  return {
    name: `Issue — ${session.employee?.name ?? 'unknown member'}`,
    rawCapture: text,
    capturedById: session.employee?.id ?? null,
    relatedProjectId: session.project?.id ?? null,
    reasonHeld: PENDING_REASON.UNCLASSIFIED,
    status: PENDING_STATUS.PENDING,
  }
}

/*
 * PROV-1 — Pending Desk Check-In resolution.
 *
 * Held check-ins write their Event Time into Raw Capture in a fixed format
 * ("Check-in at <ISO> for ..."). Extraction is a strict match against that
 * exact format written by this module — deterministic recovery of our own
 * data, not fuzzy parsing. Returns null if the item is not a held check-in.
 */
const HELD_CHECKIN_ISO = /^Check-in at (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z) /

export function extractHeldCheckInEventTime(rawCapture) {
  const m = HELD_CHECKIN_ISO.exec(String(rawCapture ?? ''))
  return m ? m[1] : null
}

/**
 * IR for the resolution act (1D authority is enforced by the caller, which
 * has live access to Employee roles). Cardinality per PROV-1: one pending
 * check-in capture → one Daily Labor Entry.
 * @param pending    { id, rawCapture }
 * @param resolution { employee:{id,name}, project:{id,name}, resolver:{id,name}, eventTimeISO }
 */
export function buildResolutionRecords(pending, resolution) {
  const { employee, project, resolver, eventTimeISO } = resolution
  return {
    laborEntry: {
      name: `${employee.name} — ${project.name} — ${dateLabel(eventTimeISO)}`,
      employeeId: employee.id,
      projectId: project.id,
      dateISO: eventTimeISO,
      // ET-3: authoritative Event Time = the ORIGINAL capture instant, full
      // precision. Check-In Time (created_time) correctly records the
      // resolution moment as System Time.
      checkedInAtEvent: eventTimeISO,
      operationalProgress: LABOR_PROGRESS.WORKING,
      // PROV-1: permanent Reference to the exact originating capture.
      originatingCaptureId: pending.id,
    },
    pendingClose: { status: 'Resolved' }, // existing staging lifecycle — closed, never deleted
    // Audit records the resolution act separately (PROV-1): the human
    // decision on the pending item, and the standard create on the entry.
    auditResolution: {
      name: `Resolution — ${employee.name} (by ${resolver.name})`,
      actorId: resolver.id,
      action: 'Approve',
      outcome: AUDIT_OUTCOME.SUCCESS,
      resourceType: 'Pending Desk Item',
      resourceReference: pending.id,
    },
    auditCreate: {
      name: `Check-in (resolved) — ${employee.name}`,
      actorId: resolver.id,
      action: AUDIT_ACTION.CREATE,
      outcome: AUDIT_OUTCOME.SUCCESS,
      resourceType: RESOURCE_TYPE_LABOR,
      // resourceReference filled with the created entry id by the writer.
    },
  }
}

/*
 * Duplicate Check-In rejection (CP-001 Duplicate Capture Handling — Reject —
 * under the One Active Workday State invariant). The rejection is never
 * silent: the user is told plainly, and the attempt is audited (Reject/
 * Blocked) against the existing active entry.
 */
export function buildDuplicateRejectAudit(session, activeRecordId) {
  return {
    name: `Check-in rejected (duplicate) — ${session.employee?.name ?? 'unknown'}`,
    actorId: session.employee?.id ?? null,
    action: 'Reject',
    outcome: 'Blocked',
    resourceType: RESOURCE_TYPE_LABOR,
    resourceReference: activeRecordId,
  }
}

/*
 * Workday Void audit (canonical: Void and Delete are distinct; a Voided
 * workday is preserved but invalid for active/payable operational use; a
 * reason is always required and lives in the Audit Record's Reason field).
 */
export function buildWorkdayVoidAudit(entryName, voider, recordId, reason) {
  return {
    name: `Workday Void — ${entryName}`,
    actorId: voider.id,
    action: AUDIT_ACTION.UPDATE, // canon: "Workday Void / Update as canon specifies"
    outcome: AUDIT_OUTCOME.SUCCESS,
    resourceType: RESOURCE_TYPE_LABOR,
    resourceReference: recordId,
    reason,
  }
}

function gpsSuffix(gps) {
  if (!gps || typeof gps.lat !== 'number' || typeof gps.lng !== 'number') return ''
  return ` (GPS ${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)} ±${Math.round(gps.accuracy ?? 0)}m)`
}

/**
 * Human duration between two ISO timestamps ("8h 12m"). Duration is DERIVED
 * and displayed rounded — it is never stored. Per ET-2, the authoritative
 * underlying instants live in the Event Time fields, independent of this
 * rounding.
 */
export function durationLabel(fromISO, toISO) {
  const ms = Math.max(0, new Date(toISO) - new Date(fromISO))
  const totalMin = Math.round(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

/**
 * FD-001 check-out. `entry` is the day's record on this device:
 *   { recordId|null, checkedInAt, held }
 * Paid time is fixed here: Date.end = checkedOutAtISO, never touched again.
 * A held day (no labor entry exists) records its check-out to the Pending
 * Desk instead, so the office sees the complete story.
 */
export function buildCheckOutRecords(session, entry, checkedOutAtISO) {
  const projectName = session.project?.name ?? 'unassigned project'
  const worked = durationLabel(entry.checkedInAt, checkedOutAtISO)

  if (entry.held || !entry.recordId) {
    return {
      held: true,
      pendingDeskItem: {
        name: `Check-out held — ${session.employee?.name ?? 'unknown member'}`,
        rawCapture: `Check-out at ${checkedOutAtISO} for ${projectName}. Worked ${worked} (from ${entry.checkedInAt}).`,
        capturedById: session.employee?.id ?? null,
        relatedProjectId: session.project?.id ?? null,
        reasonHeld: PENDING_REASON.UNCLASSIFIED,
        status: PENDING_STATUS.PENDING,
      },
      checkedOutAt: checkedOutAtISO,
    }
  }

  return {
    held: false,
    laborUpdate: {
      recordId: entry.recordId,
      checkedInAt: entry.checkedInAt,   // Date.start (unchanged)
      checkedOutAt: checkedOutAtISO,    // Date.end — paid time fixed here
      // ET-3: separate authoritative Check-Out Event Time, write-once; the
      // check-in Event Time field is never touched by this update.
      checkedOutAtEvent: checkedOutAtISO,
      operationalProgress: LABOR_PROGRESS.CHECKED_OUT,
    },
    evidence: {
      name: `Check-out — ${session.employee.name}`,
      capturedById: session.employee.id,
      relatedProjectId: session.project?.id ?? null,
      confidence: session.confidence ?? CONFIDENCE.HIGH,
      source: CHECK_IN_EVIDENCE_SOURCE,
      rawContent: `Checked out — ${projectName}, ${formatTimeLabel(checkedOutAtISO)}. Worked ${worked}.`,
    },
    audit: {
      name: `Check-out — ${session.employee.name}`,
      actorId: session.employee.id,
      action: AUDIT_ACTION.UPDATE,
      outcome: AUDIT_OUTCOME.SUCCESS,
      resourceType: RESOURCE_TYPE_LABOR,
      resourceReference: entry.recordId,
    },
    checkedOutAt: checkedOutAtISO,
  }
}

/**
 * FD-001 day close: automatic transition after the reporting window. System
 * transition, not a human capture → Audit Record only, no Evidence Record.
 * Held days have no labor entry to close; the caller closes locally only.
 */
export function buildDayCloseRecords(session, entry, closedAtISO) {
  return {
    laborUpdate: {
      recordId: entry.recordId,
      operationalProgress: LABOR_PROGRESS.DAY_CLOSED,
    },
    audit: {
      name: `Day closed — ${session.employee?.name ?? 'unknown member'}`,
      actorId: session.employee?.id ?? null,
      action: AUDIT_ACTION.UPDATE,
      outcome: AUDIT_OUTCOME.SUCCESS,
      resourceType: RESOURCE_TYPE_LABOR,
      resourceReference: entry.recordId,
    },
    closedAt: closedAtISO,
  }
}

function formatTimeLabel(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  } catch { return iso }
}
function dateLabel(iso) {
  try {
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
  } catch { return iso }
}
